import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// assets
import { ICONS } from 'assets/icons';
import Arrow from '../../../assets/customSVG/accounts/Arrow';
import { connectWhatsapp } from '../constants/index';

/**
 * Hero card guiding the WhatsApp connection journey.
 * Shows progress ring + step list + contextual copy/CTA.
 */
const WhatAppBusiness = ({ setupData, launchWhatsAppSignup }) => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(1);
  const [selectedText, setSelectedText] = useState({
    subtitle: '',
    description: '',
    content: '',
    button: '',
    key: '',
    button1: '',
  });

  const setupSteps = useMemo(
    () => connectWhatsapp.filter((step) => step.key !== 'is_campaign_created'),
    [connectWhatsapp],
  );

  const current = setupSteps.filter(
    (step) => setupData?.[step.key] === true,
  ).length;
  const percentage =
    setupSteps.length === 0 ? 0 : (current / setupSteps.length) * 100;

  useEffect(() => {
    if (setupSteps?.length > 0) {
      const firstItem = setupSteps[0];
      setSelectedId(firstItem.id);
      setSelectedText({
        subtitle: firstItem.subtitle,
        description: firstItem.description,
        button: firstItem.button,
        content: firstItem.content,
        button1: firstItem.button1,
        key: firstItem.key || '',
      });
    }
  }, [setupSteps]);

  const business_id = setupData?.business_id;

  const handleNavigate = () => {
    if (selectedText?.button === 'Create Campaign') {
      navigate('/user/campaign/all-campaigns');
    } else if (selectedText?.button === 'Start Business Verification') {
      window.open(
        `https://business.facebook.com/settings/security??business_id=${business_id}`,
      );
    } else if (selectedText?.button === 'Manage Payment') {
      window.open(
        ` https://business.facebook.com/billing_hub/accounts/details?business_id=${business_id}&placement=standalone&account_type=whatsapp-business-account`,
      );
    } else if (selectedText?.button === 'Connect with Facebook') {
      launchWhatsAppSignup();
    }
  };

  const getButtonStyle = () => 'bg-[#05805C24] text-[#05805C] cursor-auto';

  return (
    <div className="bg-gradient-to-r from-white via-[#f8f9ff] to-white rounded-2xl p-6 shadow-[0px_24px_60px_-32px_rgba(15,23,42,0.35)] border border-[var(--surface-border)] h-full flex flex-col">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#e8f7f1] grid place-items-center text-xl">
            <img src={ICONS?.whatsApp} className="w-6 h-6" />
          </div>
          <div>
            <p className="font-semibold text-[20px] text-[var(--textBlack)]">
              {current >= 3
                ? 'Your WhatsApp Business API is live 🎉'
                : 'Get your WhatsApp Business API live 🎉'}
            </p>
            <p className="text-[var(--font-600)]">
              Let's set up your GainWix account together in easy steps.
            </p>
          </div>
        </div>
        <div className="flex items-center">
          <div className="relative inline-block mr-4">
            <div className="p-2 rounded-full border-[0.35vw] border-gray-200">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(#05805C ${percentage}%, transparent ${percentage}%, transparent 100%)`,
                  WebkitMask: 'radial-gradient(transparent 60%, black 61%)',
                  mask: 'radial-gradient(transparent 60%, black 61%)',
                }}
              />
              <div className="flex items-center justify-center h-full">
                <p className="font-semibold text-gray-600 text-sm">
                  {current}/{4}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 border-t border-[var(--surface-border)] pt-4 flex-1 flex flex-col min-h-[360px]">
        <div className="flex flex-col lg:flex-row gap-8 items-stretch flex-1">
          <div className="bg-[var(--surface-muted)] rounded-xl p-3 lg:flex-1 flex flex-col justify-center gap-4">
            {setupSteps?.map((data) => (
              <div
                key={data.id}
                className={`flex flex-row items-center pointer px-3 py-3 rounded-lg transition ${
                  selectedId === data?.id
                    ? 'bg-white border border-[var(--surface-border)] shadow-sm'
                    : 'hover:bg-white'
                }`}
                onClick={() => {
                  setSelectedId(data.id);
                  setSelectedText({
                    subtitle: data.subtitle,
                    description: data.description,
                    button: data.button,
                    content: data.content,
                    button1: data.button1,
                    key: data.key || '',
                  });
                }}
              >
                <img
                  src={
                    setupData.length === 0 || setupData?.[data?.key] === false
                      ? ICONS?.homeNotComplete
                      : ICONS?.homeSuccess
                  }
                  alt="icon"
                  className="w-5"
                />

                <p className="w-100 font14px-400 text-[var(--textBlack)] pl-5">
                  {data?.title}
                </p>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center text-center px-4 lg:flex-1 justify-center py-2">
            <img
              src={ICONS?.homeWhatsapp}
              alt="homeWhatsapp"
              className="max-w-[240px]"
            />
            <p className="font18px-600 text-[var(--textBlack)] mt-3">
              {selectedText?.subtitle}
            </p>
            <p className="font16px-400 text-[var(--font-600)] pt-2">
              {setupData?.length === 0 ||
              setupData?.[selectedText?.key] === false
                ? selectedText?.description
                : selectedText?.content}
            </p>
            <div className="flex-row items-center pt-4">
              {selectedText?.button !== 'Contact Sales' ? (
                <button
                  className={` font16px-500 py-2 px-3 rounded-md flex-row items-center ${
                    setupData?.length === 0 ||
                    setupData?.[selectedText?.key] === false
                      ? 'bg-[#6940F224] text-[#6940F2] pointer'
                      : getButtonStyle()
                  }`}
                  onClick={() => {
                    if (
                      setupData?.length === 0 ||
                      setupData?.[selectedText?.key] === false
                    )
                      handleNavigate();
                  }}
                >
                  {setupData?.length === 0 ||
                  setupData?.[selectedText?.key] === false
                    ? selectedText?.button
                    : selectedText?.button1}
                  {selectedId !== 1 &&
                    (setupData?.length === 0 ||
                      setupData?.[selectedText?.key] === false) && (
                      <div className="pl-1.5">
                        <Arrow
                          color={
                            setupData?.length === 0 ||
                            setupData?.[selectedText?.key] === false
                              ? '#6940F2'
                              : 'var(--new-primary)'
                          }
                        />
                      </div>
                    )}
                </button>
              ) : (
                <span className="font16px-400 text-[var(--font-600)] italic">
                  Write us to{' '}
                  <a
                    href="https://mail.google.com/mail/u/0/?view=cm&to=contact@gainwix.com&tf=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pointer text-[#6564CA] font16px-400 italic border-b-[1px] border-[#6564CA]"
                  >
                    contact@gainwix.com
                  </a>{' '}
                  to get your Brand new verify
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatAppBusiness;
